import {useNavigate, useParams, Link} from 'react-router-dom';
import { useState, useEffect } from 'react';
import {get} from '../helpers/httpClient';
import { usePosterColors } from '../PosterContext';
import Catalog from './component/catalog';
import Article from './component/article';
import Gallery from './component/gallery';
import GalleryItem from './component/galleryItem';
import Poster from './component/poster';
import TemplateRows from './component/TemplateRows';

const pageCache = {};

function App() {
    const params = useParams();
    const navigate = useNavigate();
    const { setPosterColors } = usePosterColors();

    const slug = params['*'] || '';
    const [page, setPage] = useState(pageCache[slug]?.page || null);
    const [breadcrumbs, setBreadcrumbs] = useState(pageCache[slug]?.breadcrumbs || []);

    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const getPage = async () => {
            try {
                const response = await get('pages?slug=' + slug);
                if (response) {
                    pageCache[slug] = { page: response.page, breadcrumbs: response.breadcrumbs || [] };
                    setPage(response.page);
                    setBreadcrumbs(response.breadcrumbs || []);
                }
            } catch (error) {
                navigate('/404')
            }
        };

        getPage();
    }, [slug]);

    useEffect(() => {
        if (page?.title) {
            document.title = page.title;
        }
    }, [page?.title]);

    // A page's own type is 'poster' for standalone poster pages, but a poster can also be embedded
    // as a Layout component on any page type (e.g. the home page's hero) — in that case its config
    // lives in template.props rather than page.extra, and still needs the same body-level styling.
    const templatePosterConfig = page?.type !== 'poster'
        ? Object.values(page?.template?.props || {}).find(v => v && typeof v === 'object' && v.type === 'poster')?.config
        : null;
    const posterExtra = page?.type === 'poster' ? page.extra : templatePosterConfig;

    useEffect(() => {
        if (posterExtra) {
            document.body.classList.add('poster-page');
            const { overlayColor, textShadeColor } = posterExtra || {};
            setPosterColors({ overlayColor, textShadeColor });
            return () => {
                document.body.classList.remove('poster-page');
                setPosterColors(null);
            };
        }
    }, [posterExtra, setPosterColors]);

    useEffect(() => {
        if (!posterExtra) return;
        const { background, backgroundVertical, backgroundType } = posterExtra || {};
        if (backgroundType !== 'image') return;

        const links = [];
        const addLink = (href, media) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            if (media) link.media = media;
            document.head.appendChild(link);
            links.push(link);
        };

        if (background && backgroundVertical) {
            addLink(background, '(orientation: landscape)');
            addLink(backgroundVertical, '(orientation: portrait)');
        } else if (background || backgroundVertical) {
            addLink(background || backgroundVertical);
        }

        return () => links.forEach(l => l.remove());
    }, [posterExtra?.background, posterExtra?.backgroundVertical, posterExtra?.backgroundType]);

    if (!page) return null;

    if (page.type === 'poster') {
        return <Poster info={page} />;
    }

    const contentNode = (
        <div className="d-flex justify-content-center flex-column">
            {/* An Article's own body renders regardless of whether it also has a layout — a
                catalog component (or any other) sits alongside its intro text, not instead of it. */}
            {page.type === 'article' && <Article info={page} />}
            {!page.template && page.type === 'layoutsection' && <Catalog info={page} />}
            {page.type === 'layoutitem' && <GalleryItem info={page} isMobile={!isLargeScreen} />}
            {page.type !== 'layoutitem' && page.template && (
                <TemplateRows
                    rows={page.template.rows}
                    mobileColumn={page.template.mobile_column}
                    props={page.template.props}
                    isMobile={!isLargeScreen}
                    titleFallback={page.caption || page.title}
                    children={page.children}
                />
            )}
        </div>
    );

    const breadcrumbsNode = breadcrumbs.length > 1 && (
        <nav className="breadcrumbs">
            {breadcrumbs.map((crumb, idx) => {
                const path = new URL(crumb.url).pathname;
                const isLast = idx === breadcrumbs.length - 1;
                const isMobileHidden = idx < breadcrumbs.length - 2;
                return (
                    <span key={idx} className={isMobileHidden ? 'breadcrumb-hidden-mobile' : ''}>
                        {idx > 0 && <span className="breadcrumb-sep">›</span>}
                        {isLast
                            ? <span className="breadcrumb-current">{crumb.name}</span>
                            : <Link to={path}>{crumb.name}</Link>
                        }
                    </span>
                );
            })}
        </nav>
    );

    return (
        <div key={page.slug} className="page-container page-appear">
            <div className="page-layout">
                {breadcrumbsNode}
                <div className="page-stack">
                    <div className="page-center">{contentNode}</div>
                </div>
            </div>
        </div>
    );
}

export default App;
